import React from "react";
import { Modal, Table, InputNumber, Tooltip, Input } from "antd";
import { SearchOutlined } from "@ant-design/icons";

const { Search } = Input;

const EditItemsModal = ({
  visible,
  onCancel,
  onOk,
  confirmLoading,
  editingItems,
  setEditingItems,
  productSearchResults,
  searchingProduct,
  currentSearchIndex,
  onSearchProduct,
  onProductSelect,
}) => {
  const formatter = new Intl.NumberFormat("en-IN");

  return (
    <Modal
      title="Edit Order Items"
      visible={visible}
      onCancel={onCancel}
      onOk={onOk}
      confirmLoading={confirmLoading}
      width={1200}
      okText="Update Items"
    >
      <Table
        dataSource={editingItems}
        pagination={false}
        rowKey="item_index"
        scroll={{ x: 1000 }}
        columns={[
          {
            title: "SL",
            key: "sl",
            render: (_, __, index) => index + 1,
          },
          {
            title: "Current Product",
            key: "current_product",
            render: (_, record) => (
              <div>
                <div style={{ fontWeight: "bold" }}>{record.product_name}</div>
                <div style={{ fontSize: "12px", color: "#666" }}>
                  Warehouse: {record.warehouse_name}
                </div>
              </div>
            ),
          },
          {
            title: "Search & Select New Product",
            key: "search_product",
            render: (_, record, index) => (
              <div>
                <Search
                  placeholder="Search product by name, barcode, code..."
                  enterButton={<SearchOutlined />}
                  size="small"
                  style={{ marginBottom: 8 }}
                  onSearch={(value) => onSearchProduct(value, index)}
                  loading={searchingProduct && currentSearchIndex === index}
                  allowClear
                />

                {currentSearchIndex === index && productSearchResults.length > 0 && (
                  <div
                    style={{
                      border: "1px solid #d9d9d9",
                      borderRadius: "4px",
                      maxHeight: "200px",
                      overflowY: "auto",
                      backgroundColor: "white",
                      zIndex: 1000,
                    }}
                  >
                    {productSearchResults.map((product) => (
                      <div
                        key={product.id}
                        onClick={() => onProductSelect(product.id, index)}
                        style={{
                          padding: "8px 12px",
                          cursor: "pointer",
                          borderBottom: "1px solid #f0f0f0",
                          transition: "background-color 0.3s",
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                        }}
                        onMouseEnter={(e) =>
                          (e.currentTarget.style.backgroundColor = "#f5f5f5")
                        }
                        onMouseLeave={(e) =>
                          (e.currentTarget.style.backgroundColor = "transparent")
                        }
                      >
                        <div style={{ flex: 1 }}>
                          <div style={{ fontWeight: "500" }}>
                            {product.title || product.name}
                          </div>
                          <div style={{ fontSize: "12px", color: "#666" }}>
                            Code: {product.product_code || product.code || "N/A"}
                          </div>
                          <div style={{ fontSize: "12px", color: "#1890ff" }}>
                            Barcode: {product.barcode || "N/A"}
                          </div>
                          <div style={{ fontSize: "12px", color: "#1890ff" }}>
                            Attributes: {product.Attribute_details || "N/A"}
                          </div>
                          <div style={{ fontSize: "12px", color: "#1890ff" }}>
                            Price: {formatter.format(product.selling_price || product.price || 0)}
                          </div>
                          <div style={{ fontSize: "12px", color: "#1890ff" }}>
                            Quantity: {formatter.format(product.quantity)}
                          </div>
                        </div>
                        <div style={{ marginLeft: "12px", fontSize: "12px", color: "#52c41a" }}>
                          {product.Warehouse_name}
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {record.current_product &&
                  record.current_product.id !== record.product_location_id && (
                    <div
                      style={{
                        marginTop: 8,
                        padding: 8,
                        backgroundColor: "#f6ffed",
                        border: "1px solid #b7eb8f",
                        borderRadius: 4,
                      }}
                    >
                      <div style={{ fontSize: "12px", fontWeight: "500" }}>
                        Selected: {record.product_name}
                      </div>
                      <div style={{ fontSize: "12px", color: "#666" }}>
                        Warehouse: {record.Warehouse_name} | Price: {formatter.format(record.price)}
                      </div>
                    </div>
                  )}
              </div>
            ),
          },
          {
            title: "Warehouse",
            key: "warehouse",
            render: (_, record) => <div style={{ fontSize: "12px" }}>{record.warehouse_name}</div>,
          },
          {
            title: "Attributes",
            key: "attributes",
            render: (_, record) => {
              const attributes = record.current_product && record.current_product.attributes;

              if (!attributes || attributes.length === 0) {
                return <div style={{ fontSize: "12px" }}>N/A</div>;
              }

              return (
                <div style={{ display: "flex", flexWrap: "wrap", gap: "4px" }}>
                  {attributes.map((attr, index) => (
                    <Tooltip
                      key={index}
                      title={
                        attr.attribute
                          ? `${attr.attribute.name}: ${attr.term_name}`
                          : attr.term_name
                      }
                    >
                      <span
                        style={{
                          backgroundColor: "#f0f0f0",
                          padding: "2px 6px",
                          borderRadius: "3px",
                          fontSize: "11px",
                          border: "1px solid #d9d9d9",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {attr.term_name}
                      </span>
                    </Tooltip>
                  ))}
                </div>
              );
            },
          },
          {
            title: "Barcode",
            key: "barcode",
            render: (_, record) => (
              <div style={{ fontSize: "12px" }}>
                {record.current_product && record.current_product.barcode}
              </div>
            ),
          },
          {
            title: "Quantity",
            key: "quantity",
            render: (_, record, index) => (
              <InputNumber
                min={1}
                value={record.quantity}
                onChange={(value) => {
                  const newItems = [...editingItems];
                  newItems[index].quantity = value || 1;
                  setEditingItems(newItems);
                }}
                style={{ width: "100%" }}
              />
            ),
          },
          {
            title: "Price",
            key: "price",
            render: (_, record, index) => (
              <InputNumber
                min={0}
                value={record.price}
                onChange={(value) => {
                  const newItems = [...editingItems];
                  newItems[index].price = value || 0;
                  setEditingItems(newItems);
                }}
                style={{ width: "100%" }}
                formatter={(value) => ` ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
                parser={(value) =>
                  value ? parseFloat(value.replace(/৳\s?|(,*)/g, "")) : 0
                }
              />
            ),
          },
          {
            title: "Total",
            key: "total",
            render: (_, record) => (
              <strong>
                {formatter.format(
                  (record.quantity || 0) * (record.price || 0) - (record.spec_discount || 0)
                )}
              </strong>
            ),
          },
        ]}
        summary={(pageData) => {
          let totalQuantity = 0;
          let totalAmount = 0;
          let totalDiscount = 0;

          pageData.forEach((item) => {
            totalQuantity += item.quantity || 0;
            totalAmount += (item.quantity || 0) * (item.price || 0);
            totalDiscount += item.spec_discount || 0;
          });

          return (
            <Table.Summary.Row style={{ backgroundColor: "#fafafa" }}>
              <Table.Summary.Cell index={0} colSpan={6} align="right">
                <strong>Grand Total:</strong>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={1} align="left">
                <strong>{totalQuantity}</strong>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={2} align="left">
                <strong>{formatter.format(totalAmount)}</strong>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={4} align="left">
                <strong style={{ color: "#1890ff", fontSize: "16px" }}>
                  {formatter.format(totalAmount - totalDiscount)}
                </strong>
              </Table.Summary.Cell>
            </Table.Summary.Row>
          );
        }}
      />
    </Modal>
  );
};

export default EditItemsModal;